import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { PlusCircle, Edit, Trash2, Download, Eye, Loader2 } from 'lucide-react';
import axios from 'axios';
import SITE_CONFIG from '../config/siteConfig';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';

const DashboardPage = () => {
  const navigate = useNavigate();
  const [carousels, setCarousels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [stats, setStats] = useState({
    totalViews: 0,
    totalDownloads: 0,
    totalCarousels: 0,
    thisMonth: 0
  });

  // Fetch carousels from API
  useEffect(() => {
    fetchCarousels();
  }, []);

  const fetchCarousels = async () => {
    try {
      setLoading(true);
      setError(null);

      // Get auth token
      const token = localStorage.getItem('token') || localStorage.getItem('access_token');
      
      if (!token) {
        setError('Please log in to view your carousels');
        setLoading(false);
        return;
      }

      console.log('🔍 Fetching carousels from API...');

      // Fetch carousels
      const response = await axios.get(`${API_BASE_URL}/carousels/`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      console.log('✅ Carousels fetched:', response.data);

      // Handle both paginated and non-paginated responses
      const carouselData = response.data.results || response.data;
      setCarousels(carouselData);

      // Calculate stats
      const totalViews = carouselData.reduce((sum, c) => sum + (c.view_count || 0), 0);
      const totalDownloads = carouselData.reduce((sum, c) => sum + (c.download_count || 0), 0);
      const totalCarousels = carouselData.length;
      
      // Count carousels created this month
      const now = new Date();
      const thisMonth = carouselData.filter(c => {
        const createdDate = new Date(c.created_at);
        return createdDate.getMonth() === now.getMonth() && 
               createdDate.getFullYear() === now.getFullYear();
      }).length;

      setStats({
        totalViews,
        totalDownloads,
        totalCarousels,
        thisMonth
      });

      setLoading(false);
    } catch (err) {
      console.error('❌ Error fetching carousels:', err);
      setError(err.response?.data?.detail || 'Failed to load carousels');
      setLoading(false);
    }
  };

  const handleDelete = async (carouselId) => {
    if (!window.confirm('Are you sure you want to delete this carousel?')) {
      return;
    }

    try {
      const token = localStorage.getItem('token') || localStorage.getItem('access_token');
      
      await axios.delete(`${API_BASE_URL}/carousels/${carouselId}/`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      console.log('✅ Carousel deleted:', carouselId);
      
      // Refresh carousels list
      fetchCarousels();
    } catch (err) {
      console.error('❌ Error deleting carousel:', err);
      alert('Failed to delete carousel');
    }
  };

  const handleView = (carouselId) => {
    navigate(`/carousel/${carouselId}`);
  };


  const getPlatformName = (platform) => {
    const platforms = {
      instagram_square: 'Instagram Square',
      instagram_story: 'Instagram Story',
      instagram_feed: 'Instagram Feed',
      linkedin_post: 'LinkedIn Post',
      facebook_post: 'Facebook Post',
      twitter_post: 'Twitter Post',
    };
    return platforms[platform] || platform;
  };

  // Loading state
  if (loading) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex items-center justify-center h-64">
          <Loader2 className="h-8 w-8 animate-spin text-primary-600" />
          <span className="ml-2 text-gray-600">Loading carousels...</span>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="card p-8 text-center">
          <div className="text-red-600 mb-4">
            <p className="text-lg font-semibold">Error Loading Carousels</p>
            <p className="text-sm mt-2">{error}</p>
          </div>
          <button 
            onClick={fetchCarousels}
            className="btn-primary"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>My Carousels - Dashboard | AI Carousel</title>
        <link rel="canonical" href={`${SITE_CONFIG.siteUrl}/dashboard`} />
        <meta 
          name="description" 
          content="Generate high-quality social media carousel posts using AI. Create content, images, and captions instantly for LinkedIn and social platforms" 
        />
        <meta 
          name="keywords" 
          content="ai carousel generator, social media carousel maker, ai content for social media, carousel post generator, linkedin carousel generator" 
        />
        <meta property="og:title" content="Dashboard - AI Carousel Maker" />
        <meta property="og:url" content={`${SITE_CONFIG.siteUrl}/dashboard`} />
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Header */}
      <div className="flex justify-between items-center mb-8">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">My Carousels</h1>
          <p className="text-gray-600 mt-1">
            Manage and edit your carousel designs
          </p>
        </div>
        <Link 
          to="/generate" 
          className="btn-primary flex items-center space-x-2"
        >
          <PlusCircle className="h-5 w-5" />
          <span>Create New Carousel</span>
        </Link>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
        <div className="card p-6">
          <div className="flex items-center">
            <div className="p-2 bg-primary-100 rounded-lg">
              <Eye className="h-6 w-6 text-primary-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Total Views</p>
              <p className="text-2xl font-bold text-gray-900">{stats.totalViews.toLocaleString()}</p>
            </div>
          </div>
        </div>

        <div className="card p-6">
          <div className="flex items-center">
            <div className="p-2 bg-green-100 rounded-lg">
              <Download className="h-6 w-6 text-green-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Downloads</p>
              <p className="text-2xl font-bold text-gray-900">{stats.totalDownloads}</p>
            </div>
          </div>
        </div>

        <div className="card p-6">
          <div className="flex items-center">
            <div className="p-2 bg-blue-100 rounded-lg">
              <Edit className="h-6 w-6 text-blue-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Total Carousels</p>
              <p className="text-2xl font-bold text-gray-900">{stats.totalCarousels}</p>
            </div>
          </div>
        </div>

        <div className="card p-6">
          <div className="flex items-center">
            <div className="p-2 bg-purple-100 rounded-lg">
              <PlusCircle className="h-6 w-6 text-purple-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">This Month</p>
              <p className="text-2xl font-bold text-gray-900">{stats.thisMonth}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Carousels Grid */}
      <div className="card">
        <div className="p-6 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">Recent Carousels</h2>
        </div>
        
        {carousels.length === 0 ? (
          <div className="p-12 text-center">
            <div className="w-24 h-24 mx-auto bg-gray-100 rounded-full flex items-center justify-center mb-4">
              <PlusCircle className="h-12 w-12 text-gray-400" />
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">No carousels yet</h3>
            <p className="text-gray-600 mb-6">
              Create your first carousel to get started with beautiful social media content.
            </p>
            <Link to="/generate" className="btn-primary">
              Create Your First Carousel
            </Link>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Title
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Platform
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Slides
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Views
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Created
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {carousels.map((carousel) => (
                  <tr key={carousel.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">
                        {carousel.title}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-600">
                        {getPlatformName(carousel.platform)}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                      {carousel.slide_count || 0}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                      {carousel.view_count || 0}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                      {new Date(carousel.created_at).toLocaleDateString()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex items-center justify-end space-x-2">
                        <button
                          onClick={() => handleView(carousel.id)}
                          className="text-blue-600 hover:text-blue-900"
                          title="View Carousel"
                        >
                          <Eye className="h-4 w-4" />
                        </button>
                        {/* EDITOR DISABLED - Will be re-enabled later
                        <Link
                          to={`/editor/${carousel.id}`}
                          className="text-primary-600 hover:text-primary-900"
                          title="Edit Carousel"
                        >
                          <Edit className="h-4 w-4" />
                        </Link>
                        */}
                        <button 
                          className="text-green-600 hover:text-green-900"
                          title="Download Carousel"
                        >
                          <Download className="h-4 w-4" />
                        </button>
                        <button 
                          onClick={() => handleDelete(carousel.id)}
                          className="text-red-600 hover:text-red-900"
                          title="Delete Carousel"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
      </div>
    </>
  );
};

export default DashboardPage;
